import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import Button from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input, Textarea } from '../components/ui/Input';
import { Spinner } from '../components/ui/Spinner';

const API_BASE = process.env.REACT_APP_API_URL;

export default function TrainingModeling() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [motion, setMotion] = useState('');
  const [loadingMotion, setLoadingMotion] = useState(false);

  const [terms, setTerms] = useState([{ term: '', definition: '' }]);
  const [how, setHow] = useState('');
  const [examples, setExamples] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // feedback fields
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (!user) navigate('/login');
  }, [user, navigate]);

  const newMotion = async () => {
    setError('');
    setFeedback(null);
    setTerms([{ term: '', definition: '' }]);
    setHow('');
    setExamples('');

    setLoadingMotion(true);
    try {
      const res = await fetch(`${API_BASE}/api/generate-motion`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to generate motion');
      setMotion(data.motion);
    } catch (e) {
      console.error(e);
      setError('Could not generate a motion.');
    } finally {
      setLoadingMotion(false);
    }
  };

  const addTerm = () => setTerms(prev => [...prev, { term: '', definition: '' }]);
  const removeTerm = (i) => setTerms(prev => prev.filter((_, idx) => idx !== i));
  const updateTerm = (i, field, value) => {
    setTerms(prev => prev.map((t, idx) => (idx === i ? { ...t, [field]: value } : t)));
  };

  const submit = async () => {
    setError('');
    setFeedback(null);

    if (!motion) {
      setError('Generate a motion first.');
      return;
    }

    const cleanTerms = terms
      .map(t => ({ term: t.term.trim(), definition: t.definition.trim() }))
      .filter(t => t.term && t.definition);

    if (cleanTerms.length === 0) {
      setError('Add at least one key term with a clear definition.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/modeling-feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          motion,
          terms: cleanTerms,
          how: how.trim(),
          examples: examples.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to evaluate modeling');
      setFeedback(data);
    } catch (e) {
      console.error(e);
      setError('Failed to get feedback.');
    } finally {
      setSubmitting(false);
    }
  };

  const copyAll = async () => {
    const t = terms
      .map((x, i) => `Term ${i + 1}: ${x.term}\nDefinition: ${x.definition}`)
      .join('\n\n');
    const txt = `Motion: ${motion}\n\n${t}\n\nHow: ${how || '—'}\n\nExamples:\n${examples || '—'}`;
    try { await navigator.clipboard.writeText(txt); } catch {}
  };

  if (!user) return null;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Modeling Practice: Definitions First</h1>
          <p className="text-sm text-gray-600">
            Define key terms precisely. Add how it works only if the motion implies policy. Examples can clarify edge cases.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={newMotion} disabled={loadingMotion}>
            {loadingMotion ? <span className="inline-flex items-center gap-2"><Spinner />Generating…</span> : 'New Motion'}
          </Button>
          <Button variant="secondary" onClick={() => navigate('/training')}>Back to Training</Button>
        </div>
      </div>

      {/* Motion */}
      <Card title="Practice Setup" subtitle="Your motion for this drill">
        <label className="block text-xs font-medium text-gray-600 mb-1">Motion</label>
        <div className="rounded-lg border bg-gray-50 px-3 py-2 text-sm text-gray-800 min-h-[40px]">
          {motion || 'Click "New Motion" to begin.'}
        </div>
      </Card>

      {/* Key Terms */}
      <Card title="Key Terms" subtitle="Define essential words in the motion. Aim for clear, fair, debate-useful definitions.">
        <div className="space-y-5">
          {terms.map((t, i) => (
            <div key={i} className="rounded-lg border border-gray-200 p-4">
              <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Term</label>
                  <Input
                    placeholder="e.g., universal basic income"
                    value={t.term}
                    onChange={(e) => updateTerm(i, 'term', e.target.value)}
                  />
                </div>
                <div className="sm:col-span-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Definition</label>
                  <Textarea
                    rows={2}
                    placeholder="Definition that is clear, fair, and useful for adjudication"
                    value={t.definition}
                    onChange={(e) => updateTerm(i, 'definition', e.target.value)}
                  />
                </div>
              </div>

              <div className="mt-2 flex justify-between">
                <span className="text-xs text-gray-500">
                  Tip: Prefer criteria that the chair can apply easily. Add edge cases if helpful.
                </span>
                {terms.length > 1 && (
                  <Button variant="ghost" className="text-red-600" onClick={() => removeTerm(i)}>
                    Remove
                  </Button>
                )}
              </div>
            </div>
          ))}

          <div className="flex items-center justify-between">
            <Button variant="secondary" onClick={addTerm}>+ Add Term</Button>
            <Button variant="secondary" onClick={copyAll}>Copy All</Button>
          </div>
        </div>
      </Card>

      {/* Optional: How it would be done */}
      <Card title="How It Would Be Done (optional)" subtitle="Outline a mechanism only if the motion implies a policy or implementation.">
        <Textarea
          rows={3}
          placeholder="Briefly outline who does what, with what authority, and any key safeguards."
          value={how}
          onChange={(e) => setHow(e.target.value)}
        />
        <p className="mt-2 text-xs text-gray-500">
          Not all motions need a mechanism. Philosophical motions usually do not.
        </p>
      </Card>

      {/* Optional: Examples */}
      <Card title="Examples for Clarity (optional)" subtitle="Use 1–2 concrete cases to illustrate boundaries or edge cases.">
        <Textarea
          rows={3}
          placeholder="Example 1: … Example 2: …"
          value={examples}
          onChange={(e) => setExamples(e.target.value)}
        />
      </Card>

      <div className="flex justify-end gap-2">
        <Button onClick={submit} disabled={submitting}>
          {submitting ? <span className="inline-flex items-center gap-2"><Spinner />Evaluating…</span> : 'Submit for Feedback'}
        </Button>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      )}

      {/* Feedback */}
      {feedback && (
        <Card title="AI Feedback" subtitle="Scoring emphasizes definitions first. Mechanism and examples are graded if present.">
          <div className="text-sm text-gray-800 space-y-3">
            <div className="rounded-lg border border-gray-200 p-4 grid sm:grid-cols-5 gap-2">
              {'definitions_coverage' in feedback && (
                <p><strong>Definitions Coverage:</strong> {feedback.definitions_coverage}/10</p>
              )}
              {'definitions_precision' in feedback && (
                <p><strong>Definitions Precision:</strong> {feedback.definitions_precision}/10</p>
              )}
              {'implementation_clarity' in feedback && feedback.implementation_clarity !== undefined && (
                <p><strong>Implementation Clarity:</strong> {feedback.implementation_clarity}/10</p>
              )}
              {'examples_helpfulness' in feedback && feedback.examples_helpfulness !== undefined && (
                <p><strong>Examples Helpfulness:</strong> {feedback.examples_helpfulness}/10</p>
              )}
              {'overall' in feedback && (
                <p><strong>Overall:</strong> {feedback.overall}/10</p>
              )}
            </div>

            {feedback.comments && (
              <div className="rounded-lg border border-gray-200 p-4 whitespace-pre-wrap">{feedback.comments}</div>
            )}
            {feedback.improvement && (
              <div className="rounded-lg border border-gray-200 p-4 whitespace-pre-wrap">
                <strong>Suggested Improvement:</strong> {feedback.improvement}
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
